import { Button, IconToast, useToast } from "@coaching-culture/ui";
import { useUser } from "auth";
import { differenceInHours } from "date-fns";
import { useMyRelationships, useNudgeRelationship } from "queries/team";
import { FaPaperPlane } from "react-icons/fa";

const NUDGE_DELAY_HOURS = 24;

/**
 * State for nudging the other party of an unconfirmed relationship to
 * accept. `visible` is false unless the current user has accepted their
 * side and the other party hasn't.
 */
export function useNudge(relationshipId: string) {
  const [user] = useUser();
  const { data: relationships } = useMyRelationships();
  const nudge = useNudgeRelationship();
  const toast = useToast();

  const rel = relationships?.find((x) => x.id === relationshipId);
  const iAmFrom = rel?.from.id === user.id;
  const ownAccepted =
    rel == null ? null : iAmFrom ? rel.fromAccepted : rel.toAccepted;
  const targetAccepted =
    rel == null ? null : iAmFrom ? rel.toAccepted : rel.fromAccepted;
  const other = rel == null ? null : iAmFrom ? rel.to : rel.from;

  const visible =
    rel != null && ownAccepted != null && targetAccepted == null;

  const oldEnough =
    rel != null &&
    differenceInHours(new Date(), new Date(rel.created)) >= NUDGE_DELAY_HOURS;
  const nudgedRecently =
    rel?.lastNudgedAt != null &&
    differenceInHours(new Date(), new Date(rel.lastNudgedAt)) <
      NUDGE_DELAY_HOURS;

  const canNudge =
    oldEnough && !nudgedRecently && !nudge.isSuccess && !nudge.isLoading;

  const title =
    nudgedRecently || nudge.isSuccess
      ? "A reminder has been sent in the last 24 hours"
      : oldEnough
        ? `Email ${other?.name} a reminder to accept`
        : "You can send a reminder once the invitation is 24 hours old";

  const label =
    nudge.isSuccess || nudgedRecently ? "Reminder Sent" : "Send Reminder";

  const send = async (ev?: React.MouseEvent) => {
    ev?.stopPropagation();
    try {
      await nudge.mutateAsync(relationshipId);
      toast({
        content: (
          <IconToast
            icon={FaPaperPlane}
            iconColor="primary"
            text="Reminder Sent"
          />
        ),
      });
    } catch (err: any) {
      window.alert(
        err.response?.data ?? "Unable to send a reminder right now.",
      );
    }
  };

  return { visible, canNudge, title, label, send };
}

export type NudgeButtonProps = {
  relationshipId: string;
  small?: boolean;
  filled?: boolean;
};

export const NudgeButton = ({
  relationshipId,
  small,
  filled,
}: NudgeButtonProps) => {
  const nudge = useNudge(relationshipId);

  if (!nudge.visible) {
    return null;
  }

  return (
    <span title={nudge.title}>
      <Button
        color="primary"
        icon={FaPaperPlane}
        small={small}
        filled={filled}
        disabled={!nudge.canNudge}
        onClick={nudge.send}
      >
        {nudge.label}
      </Button>
    </span>
  );
};
